import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { gbp, toDisplay, toGbp, curSymbol } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Target, Plus, Pencil, Trash2 } from "lucide-react";
import { CardHead, Field, FormDialog } from "@/components/prohub/shared";

const EMPTY = { name: "", target: "", saved: "" };

export default function GoalsCard({ data, onChanged }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const openForm = (g) => { setEditing(g || null); setForm(g ? { name: g.name, target: String(toDisplay(g.target)), saved: String(toDisplay(g.saved)) } : EMPTY); setOpen(true); };

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChanged(); return true; } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); return false; }
  };

  const save = async () => {
    setSaving(true);
    const payload = { name: form.name, target: toGbp(form.target), saved: toGbp(form.saved || 0) };
    const ok = await run(() => (editing ? api.patch(`/pro-hub/goals/${editing.id}`, payload) : api.post("/pro-hub/goals", payload)), editing ? "Goal updated" : "Goal added");
    setSaving(false);
    if (ok) setOpen(false);
  };

  return (
    <div className="mm-glow-card p-6 h-full" data-testid="pro-goals-card">
      <CardHead icon={Target} title="Savings goals" sub="House deposit, holiday, emergency fund…"
        action={<Button data-testid="pro-add-goal" onClick={() => openForm()} size="sm" className="rounded-full bg-[#00C896] hover:bg-[#00E5AC] text-[#04140D] font-semibold"><Plus className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Add goal</span></Button>} />
      <div className="mt-5 space-y-3">
        {data.goals.map((g) => {
          const pct = Math.min(100, (g.saved / g.target) * 100);
          return (
            <div key={g.id} data-testid={`pro-goal-${g.id}`} className="rounded-xl border border-white/8 bg-[#0D0D0D] p-3.5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium truncate">{g.name}</p>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[11px] font-num ${pct >= 100 ? "text-[#00C896]" : "text-slate-400"}`}>{pct >= 100 ? "Reached" : `${Math.round(pct)}%`}</span>
                  <button data-testid={`pro-goal-edit-${g.id}`} onClick={() => openForm(g)} className="text-slate-500 hover:text-white transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`pro-goal-delete-${g.id}`} onClick={() => run(() => api.delete(`/pro-hub/goals/${g.id}`), "Goal removed")} className="text-slate-500 hover:text-rose-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="h-2 rounded-full bg-white/5 overflow-hidden mt-2.5">
                <div className="h-full rounded-full bg-[#00C896] transition-[width] duration-700" style={{ width: `${pct}%` }} />
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5 font-num">{gbp(g.saved)} of {gbp(g.target)}</p>
            </div>
          );
        })}
        {data.goals.length === 0 && <p data-testid="pro-goals-empty" className="py-6 text-center text-sm text-slate-500">No goals yet. Give your savings a purpose.</p>}
      </div>
      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit goal" : "New savings goal"} testid="pro-goal-modal" onSave={save} saving={saving}
        disabled={!form.name || !(parseFloat(form.target) > 0)} saveLabel={editing ? "Save changes" : "Add goal"}>
        <Field label="Goal name" testid="pro-goal-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="House deposit" />
        <div className="grid grid-cols-2 gap-3">
          <Field label={`Target (${curSymbol()})`} testid="pro-goal-target-input" type="number" min="1" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} placeholder="20000" />
          <Field label={`Saved so far (${curSymbol()})`} testid="pro-goal-saved-input" type="number" min="0" value={form.saved} onChange={(e) => setForm({ ...form, saved: e.target.value })} placeholder="0" />
        </div>
      </FormDialog>
    </div>
  );
}
